import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";

import { Slot } from "@radix-ui/react-slot";

import { cn } from "@lib/utils";

import { UI_SETTINGS } from "@configs/ui-settings";

const IconContainer = ({ children }: { children: React.ReactNode }) => (
	<span
		style={{ textRendering: "optimizeLegibility", WebkitFontSmoothing: "antialiased" }}
		className="color-inherit inline-block transform-none text-center align-[-0.125em] font-normal"
	>
		{children}
	</span>
);

export type TButtonTypes =
	| "primary"
	| "secondary"
	| "success"
	| "glass"
	| "action"
	| "tip"
	| "spy"
	| "error"
	| "red100"
	| "red200"
	| "text"
	| "link"
	| "ground"
	| "block"
	| "toy";

export type TSizeParams = "small" | "medium" | "large" | "auto";

const fontSizeMap: Record<string, string> = {
	small: "text-sm",
	medium: "text-base",
	large: "text-lg",
};

const lineHeightMap: Record<string, string> = {
	small: "leading-[18px]",
	medium: "leading-[18px]",
	large: "leading-[18px]",
};

const paddingSizeMap: Record<string, string> = {
	small: "px-4 py-0",
	medium: "px-6 py-0",
	large: "px-8 py-0",
};

const circlePaddingConfig: Record<string, string> = {
	small: "p-2",
	medium: "p-0.5",
	large: "p-4",
};

const borderRadiusMap: Record<string, string> = {
	medium: "rounded-lg",
	round: "rounded-[60px]",
	circle: "rounded-[50%]",
};

const heightMap: Record<TSizeParams, string> = {
	small: "h-[34px]",
	medium: "h-[40px]",
	large: "h-[50px]",
	auto: "h-auto",
};

const backgroundColorMap: Record<TButtonTypes, string> = {
	primary: "bg-[var(--color-primary)]",
	secondary: "bg-[var(--color-secondary)]",
	success: "bg-[var(--color-success)]",
	glass: "bg-[var(--color-glass)]",
	action: "bg-[var(--color-action)]",
	tip: "bg-[var(--color-tip)]",
	spy: "bg-[var(--color-spy)]",
	error: "bg-[var(--color-error)]",
	red100: "bg-[var(--color-red-100)]",
	red200: "bg-[var(--color-red-200)]",
	text: "bg-[var(--color-white)]",
	link: "bg-[var(--color-link)]",
	ground: "bg-[var(--color-ground)]",
	block: "bg-[var(--color-block)]",
	toy: "bg-[var(--color-toy)]",
};

const shadowMap: Record<TButtonTypes, string> = {
	primary: "hover:shadow-[var(--color-primary)_0px_0px_10px_2px]",
	secondary: "hover:shadow-[var(--color-secondary)_0px_0px_10px_2px]",
	success: "hover:shadow-[var(--color-success)_0px_0px_10px_2px]",
	glass: "hover:shadow-[var(--color-glass)_0px_0px_10px_2px]",
	action: "hover:shadow-[var(--color-action)_0px_0px_10px_2px]",
	tip: "hover:shadow-[var(--color-tip)_0px_0px_10px_2px]",
	spy: "hover:shadow-[var(--color-spy)_0px_0px_10px_2px]",
	error: "hover:shadow-[var(--color-error)_0px_0px_10px_2px]",
	red100: "hover:shadow-[var(--color-red-100)_0px_0px_10px_2px]",
	red200: "hover:shadow-[var(--color-red-200)_0px_0px_10px_2px]",
	text: "hover:shadow-[var(--color-white)_0px_0px_10px_2px]",
	link: "hover:shadow-[var(--color-link)_0px_0px_10px_2px]",
	ground: "hover:shadow-[var(--color-ground)_0px_0px_10px_2px]",
	block: "hover:shadow-[var(--color-block)_0px_0px_10px_2px]",
	toy: "hover:shadow-[var(--color-toy)_0px_0px_10px_2px]",
};

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
	asChild?: boolean;
	children?: React.ReactNode;
	wide?: boolean;
	type?: TButtonTypes;
	shape?: "medium" | "round" | "circle";
	size?: TSizeParams;
	animated?: boolean;
	disable?: boolean;
	icon?: React.ReactElement;
	inverse?: boolean;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/** Displays a button or a component that looks like a button
 * @prop className - The class name to apply to the button
 * @prop children - The content of the button
 * @prop icon - The icon to display on the button
 * @prop inverse - Whether to display the icon in inverse
 * @prop asChild - Whether to render the button as a child component
 * @prop wide - Whether to make the button full width
 * @prop type - The type of the button
 * @prop shape - The shape of the button
 * @prop size - The size of the button
 * @prop animated - Whether to animate the button
 * @prop disable - Whether to disable the button
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			children,
			icon,
			inverse,
			onClick,
			asChild = false,
			wide = false,
			type = "primary",
			shape = "medium",
			size = "medium",
			animated = false,
			disable = false,
			...props
		},
		ref,
	) => {
		const buttonRef = useRef<HTMLButtonElement>(null);
		const circleRef = useRef<HTMLSpanElement>(null);

		const Comp = asChild ? Slot : "button";
		const isButtonHoverAnimation = UI_SETTINGS.buttonHoverAnimation;
		const isButtonDisabled = disable || props.disabled;

		// Inline styles for ::after pseudo-element (needed for dynamic values)
		const buttonWrapperCss = useMemo(
			() => [
				!isButtonDisabled ? backgroundColorMap[type] || backgroundColorMap.primary : "",
				borderRadiusMap[shape] || borderRadiusMap.medium,
				!isButtonDisabled ? shadowMap[type] || shadowMap.primary : "",
				animated && !isButtonDisabled ? "button-glow 1.5s ease-in-out infinite alternate" : "none",
				isButtonHoverAnimation && !isButtonDisabled ? "1" : "0",
			],
			[type, shape, animated, isButtonDisabled, isButtonHoverAnimation],
		);

		const buttonClasses = useMemo(() => {
			const baseClasses = [
				wide ? "w-full" : "w-auto",
				"relative",
				"!overflow-hidden",
				"transition-[background]",
				"duration-[400ms]",
				"text-white",
				backgroundColorMap[type] || backgroundColorMap.primary,
				"font-bold",
				fontSizeMap[size],
				"outline-none",
				borderRadiusMap[shape] || borderRadiusMap.medium,
				"cursor-pointer",
				"flex",
				"justify-center",
				"items-center",
				"z-base",
				"gap-2",
				"font-[Inter]",
				lineHeightMap[size],
				heightMap[size],
			];

			if (shape === "circle") {
				baseClasses.push(circlePaddingConfig[size] || circlePaddingConfig.medium);
			} else {
				baseClasses.push(paddingSizeMap[size] || paddingSizeMap.medium);
			}

			if (type === "glass") {
				baseClasses.push("border-2", "border-solid", "border-transparent");
			}

			if (isButtonDisabled) {
				baseClasses.push("opacity-40", "cursor-no-drop");
			}

			return baseClasses;
		}, [wide, type, shape, size, isButtonDisabled]);

		const onAnimateBtn = (event: React.MouseEvent<HTMLButtonElement>) => {
			if (onClick) {
				onClick(event);
			}

			if (isButtonHoverAnimation) {
				if (buttonRef.current && circleRef.current) {
					const diameter = Math.max(buttonRef.current.clientWidth, buttonRef.current.clientHeight);
					const radius = diameter / 2;

					circleRef.current.style.width = circleRef.current.style.height = `${diameter}px`;
					circleRef.current.style.left = `${event.clientX - buttonRef.current.getBoundingClientRect().left - radius}px`;
					circleRef.current.style.top = `${event.clientY - buttonRef.current.getBoundingClientRect().top - radius}px`;
					circleRef.current.classList.add("ripple");

					const ripple = buttonRef.current.getElementsByClassName("ripple")[0];

					if (ripple) {
						ripple.remove();
					}

					buttonRef.current.appendChild(circleRef.current);
				}
			}
		};

		useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement);

		return (
			<div
				id="button-wrapper"
				className={cn(buttonWrapperCss, `relative inline-block ${wide ? "w-full" : "w-auto"}`)}
				// style={buttonWrapperCss}
			>
				<Comp
					id="custom-button"
					ref={buttonRef}
					className={cn(buttonClasses, className)}
					disabled={isButtonDisabled}
					onClick={type !== "link" ? onAnimateBtn : onClick}
					{...props}
				>
					<span
						className="absolute transform-[scale(0)] animate-ripple rounded-[50%] bg-white opacity-[0.7]"
						ref={circleRef}
					/>
					{icon && !inverse ? <IconContainer>{icon}</IconContainer> : null}

					{children}

					{icon && inverse ? <IconContainer>{icon}</IconContainer> : null}
				</Comp>
			</div>
		);
	},
);

Button.displayName = "Button";

export { Button };
